<script setup>

    // Future idea: use Bootstrap accordion for the extra Teilnehmer detail?

    import { ref, watch, onMounted, computed } from 'vue';
    import { getFromAPI, buildPageFooter, isButtonDisabled } from '../../functionCollection.js';
    import TeilnehmerTarifChange from './TeilnehmerTarifChange.vue';
    import Swal from 'sweetalert2';
    import axios from 'axios';
    import VueDatePicker from '@vuepic/vue-datepicker';

    const props = defineProps(['fetchUrl', 'userNumber', 'maxTeilnehmer', 'changeTarifUrl', 'getTeilnehmerUrl',
     'letterUrl', 'contractUrl', 'activateUrl', 'isAdmin', 'antrag', 'mediumLostUrl', 'kundenContractUrl', 'confirmMobilityKeyUrl', 'activateAndSendUrl'
    , 'confirmMobilityKeyUrlManually', 'nupsiAddUrl', 'minDate', 'maxDate']);
    const selectedTeilnehmer = ref(null);
    const newKartennummer = ref('');
    const newRfid = ref('');
    const selectedMediumType = ref('nupsi'); // 'nupsi' or 'rfid'
    const nupsiError = ref(null);
    const nupsiErrorPayload = ref(null);
    const isLoadingNupsi = ref(false);
    const teilnehmerInfo = ref(null);
    const columnsToFetch = "Teilnehmer,Monatspauschale,ma_status,Vertragsbeginn,Vertragsende,activation_date,activation_token,Tarifbezeichnung,Kartennummer,campaign,lost_nupsi_in_progress,antrag,needsLaden,needsNupsi,Tarifnummer,created_at,needsNupsiAntragMail,needsLadenVertragMail,needsNupsiVertragMail";
    const teilnehmerToChange = ref(null);
    const activatingTeilnehmer = ref(null);
    const isConfirmingKey = ref(false);
    const sendEmail = ref(true);

    // DatePicker Refs
    const newVertragsbeginn = ref(new Date());
    const minDate = ref(props.minDate);
    const maxDate = ref(props.maxDate);

    // needed for the page footer
    const currentPage = ref(1);
    const maxPageNumber = ref(1);
    const footerPageNumbers = ref(new Array());
    const isLoading = ref(false);


    const contractTeilnehmer = ref(null);

    async function confirmMobilityKeyManually(teilnehmer) {
        if (!teilnehmer) return;

        const result = await Swal.fire({
            title: 'Mobility-Key bestätigen?',
            text: `Soll der Mobility-Key für Teilnehmer ${teilnehmer.Teilnehmer} wirklich manuell bestätigt werden?`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Bestätigen',
            cancelButtonText: 'Abbrechen'
        });

        if (!result.isConfirmed) return;

        isConfirmingKey.value = true;
        try {
            const response = await axios.post(props.confirmMobilityKeyUrlManually, {
                Rechnungsempfaengernummer: props.userNumber,
                Teilnehmer: teilnehmer.Teilnehmer
            });

            if (response.status === 200) {
                Swal.fire('Erfolgreich', 'Mobility-Key wurde manuell bestätigt und eingerichtet.', 'success');
                await loadData();
            }
        } catch (e) {
            Swal.fire('Fehler', e.response?.data?.error || 'Das manuelle Bestätigen ist fehlgeschlagen.', 'error');
        } finally {
            isConfirmingKey.value = false;
        }
    }

    // get the Teilnehmer list content from TeilnehmerController@getTable, build page list footer
    const loadData = async () => {
        isLoading.value = true;
        try {
            const url = getFullUrl(props.fetchUrl);
            const response = await getFromAPI(url);
            if (response && response.meta) {
                maxPageNumber.value = response.meta.last_page;
                teilnehmerInfo.value = response.data;
                footerPageNumbers.value = buildPageFooter(currentPage.value, maxPageNumber.value);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            isLoading.value = false;
        }
    };

    onMounted(() => {
        loadData();
    });

    watch([currentPage, () => props.fetchUrl, () => props.userNumber], () => {
        loadData();
    });

    // complete the URL. the values in the last concat specify which page we want,
    // as well as which columns from Teilnehmer we need, and how many entries we want per page.
    function getFullUrl(url) {
        return url.concat('/').concat(props.userNumber).concat(`?page=${currentPage.value}&columns=${columnsToFetch}&column=Teilnehmer&order=asc&per_page=10`);
    }

    // calls new Teilnehmer info whenever a page change is requested (i.e. a page button is pressed in the footer)
    function getNewPage(newPage) {
        let page = newPage;
        if (page < 1) {
            page = 1;
        }
        currentPage.value = page;
    }

    const selectedTeilnehmerChanges = ref([]);

    const visibleTeilnehmer = computed(() => {
      if (!Array.isArray(teilnehmerInfo.value)) return [];
      if (props.isAdmin === 'true') return teilnehmerInfo.value;
      return teilnehmerInfo.value.filter(t => !!t.activation_date);
    });

    function formatDate(dateString) {
      var temp = new Date(dateString);

       return temp.toLocaleDateString('de-DE', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit'
    })
}

async function activateTeilnehmer(teilnehmer){
  if(!teilnehmer) return;
  activatingTeilnehmer.value = teilnehmer.Teilnehmer;
  try {
    const url = props.activateUrl;
    const payload = {
      Rechnungsempfaengernummer: props.userNumber,
      Teilnehmer: teilnehmer.Teilnehmer
    };
    await axios.post(url, payload);
    Swal.fire('Erfolgreich', `Teilnehmer ${teilnehmer.Teilnehmer} wurde erfolgreich aktiviert!`, 'success');
    await loadData();
  } catch (err) {
    Swal.fire('Aktivierung fehlgeschlagen', 'Die Aktivierung des Teilnehmers ist fehlgeschlagen.' , 'error');
  } finally {
    activatingTeilnehmer.value = null;
  }
}

async function activateTeilnehmerAndSend(teilnehmer){
  if(!teilnehmer) return;
  activatingTeilnehmer.value = teilnehmer.Teilnehmer;
  try {
    const url = props.activateAndSendUrl;
    const payload = {
      Rechnungsempfaengernummer: props.userNumber,
      Teilnehmer: teilnehmer.Teilnehmer
    };
    await axios.post(url, payload);
    Swal.fire('Erfolgreich', `Teilnehmer ${teilnehmer.Teilnehmer} wurde erfolgreich aktiviert und Vertrag wurde gesendet!`, 'success');
    await loadData();
  } catch (err) {
    Swal.fire('Aktivierung fehlgeschlagen', 'Die Aktivierung des Teilnehmers ist fehlgeschlagen.' , 'error');
  } finally {
    activatingTeilnehmer.value = null;
  }
}

async function confirmActivate(teilnehmer) {
  const result = await Swal.fire({
    title: 'Teilnehmer aktivieren?',
    text: `Soll Teilnehmer ${teilnehmer.Teilnehmer} aktiviert werden?${!teilnehmer.lost_nupsi_in_progress ? " Der Vertrag muss archiviert werden und ggf. an den Kunden gesendet werden!" : ""}`,
    icon: 'question',
    showCancelButton: true,
    confirmButtonText: 'Aktivieren',
    cancelButtonText: 'Abbrechen'
  });
  if (result.isConfirmed) {
    isLoading.value = true;
    await activateTeilnehmer(teilnehmer);
    isLoading.value = false;
  }
}



async function confirmActivateAndSend(teilnehmer) {
  const result = await Swal.fire({
    title: 'Teilnehmer aktivieren und Vertrag an Kunden senden?',
    text: `Berlin-Teilnehmer wird aktiviert und der Vertrag wird an den Kunden gesendet!`,
    icon: 'question',
    showCancelButton: true,
    confirmButtonText: 'Aktivieren und senden',
    cancelButtonText: 'Abbrechen'
  });
  if (result.isConfirmed) {
    isLoading.value = true;
    await activateTeilnehmerAndSend(teilnehmer);
    isLoading.value = false;
  }
}

function editParticipant(teilnehmer) {
  if (!teilnehmer) return;
  window.location.href = `/teilnehmeredit/${props.userNumber}/${teilnehmer.Teilnehmer}`;
}

function openTarifModal(teilnehmer) {
  if (!teilnehmer) return;
  teilnehmerToChange.value = teilnehmer.Teilnehmer;
  const modal = new bootstrap.Modal(document.getElementById('modalTeilnehmerTarifChange'));
  modal.show();
}

function openFutureChangesModal(teilnehmer) {
  if (!teilnehmer) return;
  selectedTeilnehmerChanges.value = Array.isArray(teilnehmer.future_changes) ? teilnehmer.future_changes : [];
  const modal = new bootstrap.Modal(document.getElementById('futureChangesModal'));
  modal.show();
}

function generateLetter(teilnehmer) {

  if (!teilnehmer) return;

  const mobilityKeyOnly = Boolean(teilnehmer.activation_date)
    && Number(teilnehmer.needsLaden) === 1;
  let url = props.letterUrl.replace(':Teilnehmer', encodeURIComponent(teilnehmer.Teilnehmer));
  url = url.replace('/false', mobilityKeyOnly ? '/true' : '/false');
  window.open(url, '_blank');
}

function openMediumLostModal(teilnehmer) {
  if (!teilnehmer) return;
  selectedTeilnehmer.value = teilnehmer;
  newKartennummer.value = '';
  newRfid.value = '';
  nupsiError.value = null;
  nupsiErrorPayload.value = null;

  if (Number(teilnehmer.needsNupsi) === 1) {
      selectedMediumType.value = 'nupsi';
  } else if (Number(teilnehmer.needsLaden) === 1) {
      selectedMediumType.value = 'rfid';
  }

  const modal = new bootstrap.Modal(document.getElementById('mediumLostModal'));
  modal.show();
}

function openMediumModal(teilnehmer) {
  if (!teilnehmer) return;
  selectedTeilnehmer.value = teilnehmer;
  newKartennummer.value = '';
  newRfid.value = '';
  nupsiError.value = null;
  nupsiErrorPayload.value = null;

  // DatePicker Reset
  newVertragsbeginn.value = new Date();
  minDate.value = props.minDate;
  maxDate.value = props.maxDate;

  // Intelligente Vorauswahl, je nachdem was fehlt
  if (Number(teilnehmer.needsNupsi) === 1) {
      selectedMediumType.value = 'rfid'; // Nupsi schon da -> wähle RFID
  } else if (Number(teilnehmer.needsLaden) === 1 || teilnehmer.has_pending_mobility_key_request) {
      selectedMediumType.value = 'nupsi'; // RFID schon da -> wähle Nupsi
  } else {
      selectedMediumType.value = 'nupsi'; // Nichts vorhanden -> Standard Nupsi (Nutzer kann frei wählen)
  }

  const modal = new bootstrap.Modal(document.getElementById('mediumModal'));
  modal.show();
}

async function submitMediumLost() {
  if (!selectedTeilnehmer.value) return;
  const teilnehmer = selectedTeilnehmer.value;

  if (selectedMediumType.value === 'nupsi' && newKartennummer.value.length > 9) {
    Swal.fire('Fehler', 'Die Kartennummer darf höchstens 9 Zeichen lang sein.', 'error');
    return;
  }

  let confirmTitle, confirmText, confirmBtn;
  if (selectedMediumType.value === 'nupsi') {
      confirmTitle = 'Nupsi ersetzen?';
      confirmText = `Durch Ihre Bestätigung wird der Teilnehmer deaktiviert und muss sich neu aktivieren.
       Bitte drucken Sie ein neues Anschreiben.${sendEmail.value ? ' Bitte beachten Sie, dass der Kunde eine E-Mail erhält, dass das Zugangsmedium ersetzt wurde.' : ''}`;
      confirmBtn = 'Bestätigen';
  } else {
      confirmTitle = 'Rfid ersetzen?';
      confirmText = `Die alte Rfid wird ungültig gesetzt. Bitte die RFID bei Lisy anlegen und die alte RFID sperren!
      ${sendEmail.value ? ' Es wird eine E-Mail an den Kunden versendet, dass das neue Medium in Kürze verschickt wird.' : ''}`;
      confirmBtn = 'Bestätigen';
  }

  const confirmResult = await Swal.fire({
    title: confirmTitle,
    text: confirmText,
    icon: 'warning',
    showCancelButton: true,
    confirmButtonText: confirmBtn,
    cancelButtonText: 'Abbrechen'
  });
  if (!confirmResult.isConfirmed) return;

    try {
      isLoadingNupsi.value = true;
      nupsiError.value = null;
      nupsiErrorPayload.value = null;

      const url = props.mediumLostUrl ? props.mediumLostUrl :  '/teilnehmer/medium-lost';
      const payload = {
        Rechnungsempfaengernummer: props.userNumber,
        Teilnehmer: teilnehmer.Teilnehmer,
        type: selectedMediumType.value,
        Email: sendEmail.value,
      };

      if (selectedMediumType.value === 'nupsi') {
          payload.Kartennummer = newKartennummer.value;
      } else {
          payload.rfid = newRfid.value;
      }

      const res = await axios.post(url, payload, { validateStatus: () => true });
        if (res.status === 200) {

        if (selectedMediumType.value === 'nupsi') {
            Swal.fire('Erfolgreich', 'Teilnehmer wurde deaktiviert. Bitte drucken Sie ein neues Anschreiben.', 'success');
        } else {
            Swal.fire('Erfolgreich', 'RFID wurde ersetzt und Kunde per E-Mail benachrichtigt.', 'success');
        }

        // hide modal
        const modalEl = document.getElementById('mediumLostModal');
        const modalInstance = bootstrap.Modal.getInstance(modalEl);
        if (modalInstance) modalInstance.hide();
            await loadData();
        } else {
        // If validation error, show inline under input
        const data = res.data || {};
        const hasFieldError = (data.Kartennummer || data.rfid) || (data.errors && (data.errors.Kartennummer || data.errors.kartennummer || data.errors.rfid));
        if (res.status === 422 || hasFieldError) {
          nupsiError.value = res;
          nupsiErrorPayload.value = (res.data && Object.keys(res.data).length) ? res.data : null;
        } else {
          Swal.fire('Fehler', 'Aktion fehlgeschlagen', 'error');
        }
      }
    } catch (err) {
      nupsiError.value = err;
      nupsiErrorPayload.value = err.response?.data || null;
      Swal.fire('Fehler', 'Aktion fehlgeschlagen', 'error');
    } finally {
      isLoadingNupsi.value = false;
    }
}

async function submitMedium() {
  if (!selectedTeilnehmer.value) return;
  const teilnehmer = selectedTeilnehmer.value;

  if (selectedMediumType.value === 'nupsi' && newKartennummer.value.length > 9) {
      Swal.fire('Fehler', 'Die Kartennummer darf höchstens 9 Zeichen lang sein.', 'error');
      return;
  }


  let confirmTitle, confirmText, confirmBtn;
  if (selectedMediumType.value === 'nupsi') {
      confirmTitle = 'Nupsi hinzufügen?';
      confirmText = 'Durch Ihre Bestätigung wird der Teilnehmer deaktiviert und muss sich freischalten. Bitte drucken Sie ein Anschreiben.';
      confirmBtn = 'Bestätigen';
  } else {
      confirmTitle = 'Mobility-Key hinzufügen?';
      confirmText = 'Es wird eine E-Mail zum Bestätigen an den Kunden gesendet.';
      confirmBtn = 'E-Mail versenden';
  }

  const confirmResult = await Swal.fire({
    title: confirmTitle,
    text: confirmText,
    icon: 'warning',
    showCancelButton: true,
    confirmButtonText: confirmBtn,
    cancelButtonText: 'Abbrechen'
  });
  if (!confirmResult.isConfirmed) return;

    try {
      isLoadingNupsi.value = true;
      nupsiError.value = null;
      nupsiErrorPayload.value = null;

      const url = selectedMediumType.value === 'nupsi' ? props.nupsiAddUrl : props.confirmMobilityKeyUrl;

      const payload = {
        Rechnungsempfaengernummer: props.userNumber,
        Teilnehmer: teilnehmer.Teilnehmer,
      };

      if (selectedMediumType.value === 'nupsi') {
          payload.Kartennummer = newKartennummer.value;

          if (newVertragsbeginn.value) {
              const d = newVertragsbeginn.value;
              const yyyy = d.getFullYear();
              const mm = String(d.getMonth() + 1).padStart(2, '0');
              const dd = String(d.getDate()).padStart(2, '0');
              payload.Vertragsbeginn = `${yyyy}-${mm}-${dd}`;
          }

      }

      const res = await axios.post(url, payload, { validateStatus: () => true });
      if (res.status === 200) {

        if (selectedMediumType.value === 'nupsi') {
            Swal.fire('Erfolgreich', 'Nupsi wurde hinzugefügt. Bitte drucken Sie ein neues Anschreiben.', 'success');
        } else {
            Swal.fire('Erfolgreich', 'Email zum Bestätigen wird an den Kunden gesendet!', 'success');
        }

        // hide modal
        const modalEl = document.getElementById('mediumModal');
        const modalInstance = bootstrap.Modal.getInstance(modalEl);
        if (modalInstance) modalInstance.hide();
        await loadData();
      } else {
        // If validation error, show inline under input
        const data = res.data || {};
        const hasFieldError = (data.Kartennummer || data.rfid || data.Vertragsbeginn) || (data.errors && (data.errors.Kartennummer || data.errors.kartennummer || data.errors.rfid || data.errors.Vertragsbeginn));
        if (res.status === 422 || hasFieldError) {
          nupsiError.value = res;
          nupsiErrorPayload.value = (res.data && Object.keys(res.data).length) ? res.data : null;
        } else {
          Swal.fire('Fehler', data.error || 'Aktion fehlgeschlagen', 'error');
        }
      }
    } catch (err) {
      nupsiError.value = err;
      nupsiErrorPayload.value = err.response?.data || null;
      Swal.fire('Fehler', 'Aktion fehlgeschlagen', 'error');
    } finally {
      isLoadingNupsi.value = false;
    }
}

function printCustomerContract(teilnehmer) {
  if (!teilnehmer || !teilnehmer.antrag) return;

  // Use the participant's specific antrag ID if available, otherwise fallback to the primary one
  const antragId = (typeof teilnehmer.antrag !== 'boolean') ? teilnehmer.antrag : props.antrag;
  let url = props.kundenContractUrl.replace(':id', encodeURIComponent(antragId));

  window.open(url, '_blank');
}

function openContractModal(teilnehmer) {
    if (!teilnehmer) return;
    contractTeilnehmer.value = teilnehmer;
    const modal = new bootstrap.Modal(document.getElementById('contractModal'));
    modal.show();
}

function printContract(teilnehmer, medium, manuell = false) {
  if (!teilnehmer || !medium) return;
  let url = props.contractUrl.replace(':Teilnehmer', encodeURIComponent(teilnehmer.Teilnehmer));
  url = url.replace('/false', manuell ? '/true' : '/false');

  if (manuell) {
    Swal.fire({
      title: 'Markieren?',
      text: `Soll Teilnehmer markiert werden als "hat Vertragsunterlagen erhalten"?`,
      icon: 'question',
      showDenyButton: true,
      confirmButtonText: 'Ja',
      denyButtonText: 'Nein',
      reverseButtons: true
    }).then((result) => {
      if (!result.isConfirmed && !result.isDenied) {
        return;
      }

      const customerReceived = result.isConfirmed ? 1 : 0;
      const finalUrl = `${url}?medium=${encodeURIComponent(medium)}&customer_received=${customerReceived}`;
      window.open(finalUrl, '_blank');
    });
    return;
  }

  window.open(`${url}?medium=${encodeURIComponent(medium)}`, '_blank');
}

async function sendContractAndArchive(teilnehmer, medium, manuell = false) {
    if (!teilnehmer) return;
    const result = await Swal.fire({
        title: 'Vertrag senden?',
        text: `Soll der Vertrag für Teilnehmer ${teilnehmer.Teilnehmer} an den Kunden gesendet werden?`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Senden',
        cancelButtonText: 'Abbrechen'
    });
    if (!result.isConfirmed) return;

    try {
        const url = '/teilnehmer/contract/send';
        const payload = { Rechnungsempfaengernummer: props.userNumber, Teilnehmer: teilnehmer.Teilnehmer, manuell, medium };
        const res = await axios.post(url, payload, { validateStatus: () => true });
        if (res.status === 200) {
          Swal.fire('Erfolgreich', 'Vertrag wurde an den Kunden gesendet und archiviert.', 'success');
          await loadData();
        } else {
            Swal.fire('Fehler', 'Senden fehlgeschlagen', 'error');
        }
    } catch (err) {
        Swal.fire('Fehler','Senden fehlgeschlagen', 'error');
    }
}

function getChangeStatus(changeDate) {
    const today = new Date();
    const changeTime = new Date(changeDate);
    const diffDays = Math.ceil((changeTime - today) / (1000 * 60 * 60 * 24));

    if (diffDays <= 5) return 'danger';
    if (diffDays <= 30) return 'warning';
    return 'normal';
}

async function handleTarifChanged({ reload }) {
    if (reload) {
        try {
            await loadData();

            const modalElement = document.getElementById('modalTeilnehmerTarifChange');
            const modalInstance = bootstrap.Modal.getInstance(modalElement);
            if (modalInstance) {
                modalInstance.hide();
                modalElement.addEventListener('hidden.bs.modal', () => {
                    modalElement.removeAttribute('aria-hidden');
                }, { once: true });
            }
        } catch (error) {
            console.error('Error reloading data:', error);
        }
    }
}

const isLoadingDelete = ref(false);

async function deleteTarifChange(id) {
  Swal.fire({
    title: "Tarifänderung löschen?",
    text: "Wenn Sie eine wartende Tarifänderung löschen, wird diese nicht mehr durchgeführt.",
    icon: 'warning',
    confirmButtonText: "Löschen",
    confirmButtonColor: "#e64942",
    showCancelButton: true,
    cancelButtonText: "Abbrechen",
    focusCancel: true,
    reverseButtons: true,
  }).then(async (result) => {
    if (result.isConfirmed) {
      isLoadingDelete.value = true;
      try {
        const url = `/tarifaendernliste/${id}`;
        const res = await axios.delete(url);
        if (res.status === 200) {
          Swal.fire({
            title: 'Löschen erfolgreich',
            text: 'Tarifänderung wurde erfolgreich gelöscht!',
            icon: 'success',
          }).then(() => {
            teilnehmerInfo.value = teilnehmerInfo.value.map(teilnehmer => {
              if (teilnehmer.future_changes) {
                teilnehmer.future_changes = teilnehmer.future_changes.filter(change => change.id !== id);

                if (teilnehmer.future_changes.length === 0) {
                  teilnehmer.ma_status = Number(teilnehmer.Tarifnummer) === 84 ? 'gekündigt' : 'gültig';
                }
              }
              return teilnehmer;
            });
            selectedTeilnehmerChanges.value = selectedTeilnehmerChanges.value.filter(change => change.id !== id);
            selectedTeilnehmerChanges.value = [...selectedTeilnehmerChanges.value];
        });
        }
      } catch (error) {
        Swal.fire({
          title: 'Löschen fehlgeschlagen!',
          text: 'Leider ist das Löschen fehlgeschlagen. ' + (error.response?.data?.message || ''),
          icon: 'error',
        });
      } finally {
        isLoadingDelete.value = false;
      }
    }
  });
}

async function sendeAntrag(teilnehmer) {
    Swal.fire({
    title: "Sende Email an Kunden?",
    text: 'Durch Ihre Bestätigung wird eine "Willkommens-Mail" an den Kunden geschickt.',
    icon: 'warning',
    confirmButtonText: "Email senden",
    showCancelButton: true,
    cancelButtonText: "Abbrechen",
    focusCancel: true,
    reverseButtons: true,
  }).then(async (result) => {
    if (result.isConfirmed) {
      isLoading.value = true;

        const url = `teilnehmer/sende-antrag-mail`;
        const response = await axios.post(url, {
                rechnungsempfaengernummer: props.userNumber,
                teilnehmernummer: teilnehmer.Teilnehmer,
        });
        if (response.status === 200) {
          Swal.fire({
            title: 'Email wurde erfolgreich versendendet!',
            text: 'Email wurde erfolgreich an den Kunden versendendet!',
            icon: 'success',
          })

          teilnehmer.needsNupsiAntragMail = 0;

        } else {
            Swal.fire({
                title: 'Senden fehlgeschlagen!',
                text: 'Leider ist das versenden der Email fehlgeschlagen. ' + (response.data.error),
                icon: 'error',
            });
        }

        isLoading.value = false;

    }
  });

}

</script>

<template>

    <div class="well">
        <div class="main-table" v-if="teilnehmerInfo">
            <div>
                <table class="table">
                    <thead>
                        <tr>
                            <th class="table-head">ID</th>
                            <th class="table-head">Monatspauschale</th>
                            <th v-if="props.isAdmin === 'true'" class="table-head"> Antrag </th>
                            <th class="table-head">Status</th>
                            <th class="table-head">Aktionen</th>
                        </tr>
                    </thead>

                    <tbody v-for="teilnehmer of visibleTeilnehmer" :key="teilnehmer.Teilnehmer">
                        <tr class="m-datatable__row">
                            <td>
                              <span v-if="props.isAdmin === 'true'"
                                :class="['status-dot', teilnehmer.activation_date ? 'status-active' : 'status-inactive']"
                                :title="teilnehmer.activation_date ? 'aktiv' : 'inaktiv'">
                              </span>
                              <span style="margin-left:8px">{{ teilnehmer.Teilnehmer }}</span>
                            </td>
                            <td>{{ teilnehmer.Monatspauschale }}</td>
                            <td v-if="props.isAdmin === 'true'">{{ teilnehmer.antrag }} </td>
                            <td>{{ teilnehmer.ma_status }}</td>
                            <td >
                                <div style="display:flex; align-items: flex-end;justify-content: flex-end;">
                                  <div v-if="props.isAdmin === 'true'" class="dropdown">
                                    <button
                                      class="btn btn-primary dropdown-toggle"
                                      type="button"
                                      :id="'actionsDropdown' + teilnehmer.Teilnehmer"
                                      data-bs-toggle="dropdown"
                                      aria-expanded="false"
                                      :disabled="isLoading ? true : null"
                                    >
                                      Aktionen
                                    <i :class="[isLoading ? 'fa-solid fa-spinner fa-spin' : '']" aria-hidden="true"></i>
                                    </button>
                                    <ul class="dropdown-menu dropdown-menu-end" style="padding: 5px; border: groove;" :aria-labelledby="'actionsDropdown' + teilnehmer.Teilnehmer">
                                      <li><a class="dropdown-item" href="#" @click.prevent="editParticipant(teilnehmer)">Teilnehmer bearbeiten</a></li>
                                      <li><a class="dropdown-item" href="#" @click.prevent="openTarifModal(teilnehmer)">Tarif anpassen</a></li>
                                      <li v-if="Array.isArray(teilnehmer.future_changes) && teilnehmer.future_changes.length > 0">
                                        <a class="dropdown-item" href="#" @click.prevent="openFutureChangesModal(teilnehmer)">Anstehende Tarifänderungen</a>
                                      </li>
                                      <li v-if="!teilnehmer.activation_date && !(teilnehmer.Tarifnummer == 167)"><a class="dropdown-item" href="#" @click.prevent="confirmActivate(teilnehmer)">Manuell aktivieren</a></li>
                                      <li v-else-if="!teilnehmer.activation_date && teilnehmer.Tarifnummer == 167"><a class="dropdown-item" href="#" @click.prevent="confirmActivateAndSend(teilnehmer)">Aktivieren und Vertrag senden</a></li>
                                      <!--<li v-if="!teilnehmer.activation_date && teilnehmer.needsNupsiAntragMail"><a class="dropdown-item" href="#" @click.prevent="sendeAntrag(teilnehmer)">Sende "wird in Kürze verschickt"-Mail</a></li>-->
                                      <li v-if="!teilnehmer.activation_date  && !teilnehmer.lost_nupsi_in_progress"><a :style="'color:' + (teilnehmer.needsNupsiAntragMail === 0 ? '#ff5757' : '')" class="dropdown-item" href="#" @click.prevent="sendeAntrag(teilnehmer)">"Willkommens-Mail" versenden</a></li>
                                      <li v-if="(((!teilnehmer.activation_date) && teilnehmer.activation_token) || (teilnehmer.lost_nupsi_in_progress && Number(teilnehmer.Tarifnummer) != 84) || (teilnehmer.activation_date && Number(teilnehmer.needsLaden) === 1)) && (!teilnehmer.has_pending_mobility_key_request)  "><a class="dropdown-item" href="#" @click.prevent="generateLetter(teilnehmer)">Anschreiben generieren</a></li>
                                      <li v-if="teilnehmer.activation_date && teilnehmer.antrag && Number(teilnehmer.Tarifnummer) != 84"><a class="dropdown-item" href="#" @click.prevent="printCustomerContract(teilnehmer)">Vertrag generieren (Kundenantrag)</a></li>
                                      <li v-if="teilnehmer.activation_date  && Number(teilnehmer.Tarifnummer) != 84 && (Number(teilnehmer.needsNupsi) === 1 || Number(teilnehmer.needsLaden) === 1)">
                                        <a class="dropdown-item" href="#" @click.prevent="openContractModal(teilnehmer)">Verträge verwalten</a>
                                      </li>

                                      <!-- Zugangsmedium hinzufügen (Nupsi oder RFID) -->
                                      <li v-if="teilnehmer.activation_date && (Number(teilnehmer.needsNupsi) != 1 || (Number(teilnehmer.needsLaden) != 1 && !teilnehmer.has_pending_mobility_key_request))">
                                          <a class="dropdown-item" href="#" @click.prevent="openMediumModal(teilnehmer)">Zugangsmedium hinzufügen</a>
                                      </li>

                                      <!-- Zugangsmedium ersetzen -->
                                      <li v-if="teilnehmer.activation_date && (Number(teilnehmer.needsNupsi) === 1 || Number(teilnehmer.needsLaden) === 1)">
                                          <a class="dropdown-item" href="#" @click.prevent="openMediumLostModal(teilnehmer)">Zugangsmedium ersetzen</a>
                                      </li>

                                      <!-- Manuell Mobility-Key bestätigen (bei ausstehender Anfrage) -->
                                      <li v-if="teilnehmer.activation_date && teilnehmer.has_pending_mobility_key_request">
                                        <a class="dropdown-item" href="#" @click.prevent="confirmMobilityKeyManually(teilnehmer)">
                                            <i v-if="isConfirmingKey" class="fa-solid fa-spinner fa-spin me-1"></i>
                                            Manuell Mobility-Key bestätigen
                                        </a>
                                      </li>

                                    </ul>
                                  </div>
                                  <div v-else style="display:flex;">
                                    <button
                                      type="button"
                                      class="btn btn-primary"
                                      data-bs-toggle="modal"
                                      data-bs-target="#modalTeilnehmerTarifChange"
                                      @click="teilnehmerToChange = teilnehmer.Teilnehmer"
                                    >
                                      Tarif anpassen
                                    </button>
                                  </div>
                                  <div style=" margin-left: 3px;">
                                      <i  class="fa-solid fa-caret-down fa-lg"
                                          data-bs-toggle="collapse"
                                          :data-bs-target="'#dropdown' + teilnehmer.Teilnehmer"
                                          aria-expanded="false"
                                          :aria-controls="'dropdown' + teilnehmer.Teilnehmer">
                                      </i>
                                  </div>
                                </div>
                            </td>
                        </tr>
                        <!-- sub table for additional info, deployed by the icon above.
                            while the class is called "dropdown", it is not actually a Bootstrap dropdown, but instead a collapse.
                            "Historisch gewachsen." Changing the name means changing the CSS, a risk I am not willing to take because
                            it might break tons of other things.
                        -->
                        <tr class="table-dropdown-row collapse"
                            :id="'dropdown' + teilnehmer.Teilnehmer">
                            <div class="table-dropdown"
                                style="height: 160.2px;">
                                <tr>
                                    <th>Vertragsbeginn:</th>
                                    <td>{{ teilnehmer.Vertragsbeginn }}</td>
                                </tr>
                                <tr  v-if="props.isAdmin === 'true'">
                                    <th>Vertragsende:</th>
                                    <td>{{ teilnehmer.Vertragsende }}</td>
                                </tr>
                                <tr>
                                    <th>Tarifbezeichnung:</th>
                                    <td>{{ teilnehmer.Tarifbezeichnung }}</td>
                                </tr>
                                <tr>
                                    <th>Kartennummer:</th>
                                    <td>{{ teilnehmer.Kartennummer }}</td>
                                </tr>
                            </div>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>

        <!-- The page selection footer. Each button is disabled under specific conditions, detailed at the corresponding functions. -->
        <div class="table-footer middle">
            <ul class="pagination">
                <li class="page-item">
                    <button class="page-link" :disabled="isButtonDisabled('start', currentPage, maxPageNumber)" @click="getNewPage(currentPage - 1)">
                        <i class="fas fa-lg fa-caret-left"></i>
                    </button>
                </li>
                <li v-for="page in footerPageNumbers" :key="page" :class="{ 'active' : page == currentPage }" >
                    <button class="page-link" @click="getNewPage(page)" :disabled="isButtonDisabled(page, currentPage, maxPageNumber)">{{ page }}</button>
                </li>
                <li class="page-item">
                    <button class="page-link" @click="getNewPage(currentPage + 1)" :disabled="isButtonDisabled('end', currentPage, maxPageNumber)">
                        <i class="fas fa-lg fa-caret-right"></i>
                    </button>
                </li>
            </ul>
        </div>
    </div>
    <!-- Modal opened with the Tarif anpassen button. -->
    <div class="modal fade"
         id="modalTeilnehmerTarifChange"
         tabindex="-1"
         role="dialog"
         aria-labelledby="modalTarifChangeTitle">
        <div class="modal-dialog" role="document">
            <div class="modal-content">
                <div class="modal-header">
                    <h4 class="modal-title" id="modalTarifChangeTitle">Tarif anpassen</h4>
                    <button type="button"
                            class="btn-close"
                            data-bs-dismiss="modal"
                            aria-label="Schließen">
                    </button>
                </div>
                <div class="modal-body">
                    <TeilnehmerTarifChange
                        :changeTarifUrl="props.changeTarifUrl"
                        :getTeilnehmerUrl="props.getTeilnehmerUrl"
                        :isAdmin="props.isAdmin"
                        :teilnehmerID="teilnehmerToChange"
                        @tarifChanged="handleTarifChanged"
                    />
                </div>
            </div>
        </div>
    </div>

    <div class="modal fade" id="futureChangesModal" tabindex="-1">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Anstehende Tarifänderungen</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
          </div>
          <div class="modal-body">
            <table class="table">
              <thead>
                <tr>
                  <th>Datum</th>
                  <th>Neuer Tarif</th>
                  <th>Monatspauschale</th>
                  <th v-if="props.isAdmin === 'true'">Löschen</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="change in selectedTeilnehmerChanges"
                    :key="change.id"
                    :class="{'table-warning': getChangeStatus(change.change_date) === 'warning',
                            'table-danger': getChangeStatus(change.change_date) === 'danger'}">
                  <td>{{ formatDate(change.change_date) }}</td>
                  <td>{{ change.tarif_name }}</td>
                  <td>{{ change.price }} €</td>
                  <td v-if="props.isAdmin === 'true'">
                    <button
                        class="btn btn-danger btn-sm"
                        @click="deleteTarifChange(change.id)"
                        :disabled="isLoadingDelete"
                    >
                        <i class="fa fa-trash"></i>
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <!-- Contract actions modal -->
    <div class="modal fade" id="contractModal" tabindex="-1" aria-hidden="true">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Verträge verwalten</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Schließen"></button>
          </div>
          <div class="modal-body" v-if="contractTeilnehmer">
            <p class="mb-3">Teilnehmer {{contractTeilnehmer.Teilnehmer}}</p>

            <div v-if="Number(contractTeilnehmer.needsNupsi) === 1" class="contract-action-group div-button-modal-contract mb-4">
              <h6>Parkvertrag {{contractTeilnehmer.needsNupsiVertragMail ? "(wurde noch nicht versendet oder gedruckt)" : ""}}</h6>
              <button type="button" class="btn btn-primary" @click="printContract(contractTeilnehmer, 'nupsi')">
                <i class="fa-solid fa-file-pdf me-1"></i> Ohne Unterschrift generieren
              </button>
              <button type="button" class="btn btn-primary" @click="printContract(contractTeilnehmer, 'nupsi', true)">
                <i class="fa-solid fa-file-signature me-1"></i> Mit Unterschrift generieren
              </button>
              <button type="button" class="btn btn-primary" @click="sendContractAndArchive(contractTeilnehmer, 'nupsi')">
                <i class="fa-solid fa-paper-plane me-1"></i> Senden und archivieren
              </button>
            </div>

            <div v-if="Number(contractTeilnehmer.needsLaden) === 1" class="contract-action-group div-button-modal-contract">
              <h6>Ladevertrag {{contractTeilnehmer.needsLadenVertragMail ? "(wurde noch nicht versendet oder gedruckt)" : ""}}</h6>
              <button type="button" class="btn btn-primary" @click="printContract(contractTeilnehmer, 'laden')">
                <i class="fa-solid fa-file-pdf me-1"></i> Ohne Unterschrift generieren
              </button>
              <button type="button" class="btn btn-primary" @click="printContract(contractTeilnehmer, 'laden', true)">
                <i class="fa-solid fa-file-signature me-1"></i> Mit Unterschrift generieren
              </button>
              <button type="button" class="btn btn-primary" @click="sendContractAndArchive(contractTeilnehmer, 'laden')">
                <i class="fa-solid fa-paper-plane me-1"></i> Senden und archivieren
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Medium modal -->
    <div class="modal fade" id="mediumModal" tabindex="-1" aria-hidden="true">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Zugangsmedium hinzufügen</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Schließen"></button>
          </div>
          <div class="modal-body">
            <div class="d-flex justify-content-center gap-1 mb-4">
              <label
                class="modern-radio-card"
                :class="{ 'disabled': Number(selectedTeilnehmer?.needsNupsi) === 1 }"
              >
                <input
                  type="radio"
                  name="addMediumType"
                  value="nupsi"
                  v-model="selectedMediumType"
                  class="d-none"
                  :disabled="Number(selectedTeilnehmer?.needsNupsi) === 1"
                />
                <div class="card-content">
                  <span class="card-title">Nupsi</span>
                  <i :class="selectedMediumType === 'nupsi' ? 'fa-solid fa-circle-dot' : 'fa-regular fa-circle'"></i>
                </div>
              </label>
              <!-- Option: RFID -->
              <label
                class="modern-radio-card"
                :class="{ 'disabled': Number(selectedTeilnehmer?.needsLaden) === 1 || selectedTeilnehmer?.has_pending_mobility_key_request }"
              >
                <input
                  type="radio"
                  name="addMediumType"
                  value="rfid"
                  v-model="selectedMediumType"
                  class="d-none"
                  :disabled="Number(selectedTeilnehmer?.needsLaden) === 1 || selectedTeilnehmer?.has_pending_mobility_key_request"
                />
                <div class="card-content">
                  <span class="card-title">RFID</span>
                  <i :class="selectedMediumType === 'rfid' ? 'fa-solid fa-circle-dot' : 'fa-regular fa-circle'"></i>
                </div>
              </label>
            </div>

            <div v-if="selectedMediumType === 'nupsi'">
              <p>Durch Ihre Bestätigung wird der Teilnehmer deaktiviert und muss sich neu aktivieren.</p>
              <p>Bitte drucken Sie ein Anschreiben.</p>
              <p>Falls ein Mobility-Key vorhanden ist, kann dieser trotzdem weiterverwendet werden.</p>
              <div class="mb-3">
                <label class="form-label"><strong>Kartennummer</strong></label>
                <input type="text" maxlength="9" :class="['form-control', (nupsiErrorPayload && (nupsiErrorPayload.Kartennummer || nupsiErrorPayload.errors && (nupsiErrorPayload.errors.Kartennummer || nupsiErrorPayload.errors.kartennummer))) ? 'is-invalid' : '']" v-model="newKartennummer" placeholder="Kartennummer eingeben">
                <div class="invalid-feedback" v-if="nupsiErrorPayload && (nupsiErrorPayload.Kartennummer || (nupsiErrorPayload.errors && (nupsiErrorPayload.errors.Kartennummer || nupsiErrorPayload.errors.kartennummer)))">
                  {{ nupsiErrorPayload.Kartennummer ? (Array.isArray(nupsiErrorPayload.Kartennummer) ? nupsiErrorPayload.Kartennummer[0] : nupsiErrorPayload.Kartennummer) : (nupsiErrorPayload.errors && (nupsiErrorPayload.errors.Kartennummer ? nupsiErrorPayload.errors.Kartennummer[0] : nupsiErrorPayload.errors.kartennummer[0])) }}
                </div>
                <label class="form-label mt-3"><strong>Vertragsbeginn</strong></label>
                <VueDatePicker
                    v-model="newVertragsbeginn"
                    text-input
                    format="dd.MM.yyyy"
                    locale="de"
                    :enable-time-picker="false"
                    selectText="Auswählen"
                    cancelText="Abbrechen"
                    id="vertragsbeginn"
                    :min-date="minDate"
                    :max-date="maxDate"
                    :input-class="(nupsiErrorPayload && (nupsiErrorPayload.Vertragsbeginn || (nupsiErrorPayload.errors && nupsiErrorPayload.errors.Vertragsbeginn))) ? 'form-control is-invalid' : 'form-control'"
                    utc
                />
                <div class="invalid-feedback d-block" v-if="nupsiErrorPayload && (nupsiErrorPayload.Vertragsbeginn || (nupsiErrorPayload.errors && nupsiErrorPayload.errors.Vertragsbeginn))">
                    {{ nupsiErrorPayload.Vertragsbeginn ? (Array.isArray(nupsiErrorPayload.Vertragsbeginn) ? nupsiErrorPayload.Vertragsbeginn[0] : nupsiErrorPayload.Vertragsbeginn) : (nupsiErrorPayload.errors && nupsiErrorPayload.errors.Vertragsbeginn ? nupsiErrorPayload.errors.Vertragsbeginn[0] : '') }}
                </div>
            </div>
            </div>

            <div v-if="selectedMediumType === 'rfid'">
              <p>Dem Kunden wird eine E-Mail mit den Vertragsbedingungen gesendet. Durch Klick auf den Link in der E-Mail stimmt der Kunde den Bedingungen zu und schaltet das Medium direkt frei.
              </p>
            </div>

          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-primary" data-bs-dismiss="modal">Abbrechen</button>
            <button type="button" class="btn btn-primary" :disabled="isLoadingNupsi" @click="submitMedium">
                <i :class="[isLoadingNupsi ? 'fa-solid fa-spinner fa-spin' : '']"></i>
                Zugangsmedium hinzufügen
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Medium lost modal -->
    <div class="modal fade" id="mediumLostModal" tabindex="-1" aria-hidden="true">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Zugangsmedium ersetzen</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Schließen"></button>
          </div>
          <div class="modal-body">
            <div class="d-flex justify-content-center gap-1 mb-4">
              <label
                class="modern-radio-card"
                :class="{ 'disabled': Number(selectedTeilnehmer?.needsNupsi) !== 1 }"
              >
                <input
                  type="radio"
                  name="lostMediumType"
                  value="nupsi"
                  v-model="selectedMediumType"
                  class="d-none"
                  :disabled="Number(selectedTeilnehmer?.needsNupsi) !== 1"
                />
                <div class="card-content">
                  <span class="card-title">Nupsi</span>
                  <i :class="selectedMediumType === 'nupsi' ? 'fa-solid fa-circle-dot' : 'fa-regular fa-circle'"></i>
                </div>
              </label>

              <!-- Option: RFID -->
              <label
                class="modern-radio-card"
                :class="{ 'disabled': Number(selectedTeilnehmer?.needsLaden) !== 1 }"
              >
                <input
                  type="radio"
                  name="lostMediumType"
                  value="rfid"
                  v-model="selectedMediumType"
                  class="d-none"
                  :disabled="Number(selectedTeilnehmer?.needsLaden) !== 1"
                />
                <div class="card-content">
                  <span class="card-title">RFID</span>
                  <i :class="selectedMediumType === 'rfid' ? 'fa-solid fa-circle-dot' : 'fa-regular fa-circle'"></i>
                </div>
              </label>
            </div>

            <div v-if="selectedMediumType === 'nupsi'">
              <p>Durch Ihre Bestätigung wird der Teilnehmer deaktiviert und muss sich neu aktivieren. Bitte drucken Sie ein neues Anschreiben.
                    Bitte beachten Sie, dass der Kunde eine E-Mail erhält, dass das Zugangsmedium ersetzt wurde.
              </p>
              <div class="mb-3">
                <label class="form-label">Neue Kartennummer</label>
                <input type="text" maxlength="9" :class="['form-control', (nupsiErrorPayload && (nupsiErrorPayload.Kartennummer || nupsiErrorPayload.errors && (nupsiErrorPayload.errors.Kartennummer || nupsiErrorPayload.errors.kartennummer))) ? 'is-invalid' : '']" v-model="newKartennummer" placeholder="Neue Kartennummer eingeben">
                <div style="margin-top: 2em;display: flex;align-items: center;">
                    <div class="checkbox-container">
                        <input type="checkbox" v-model="sendEmail">
                        <div class="check"></div>
                    </div>
                    <div style="margin-left: 5px;">Kunden benachrichtigen?</div>
                </div>
                <div class="invalid-feedback" v-if="nupsiErrorPayload && (nupsiErrorPayload.Kartennummer || (nupsiErrorPayload.errors && (nupsiErrorPayload.errors.Kartennummer || nupsiErrorPayload.errors.kartennummer)))">
                  {{ nupsiErrorPayload.Kartennummer ? (Array.isArray(nupsiErrorPayload.Kartennummer) ? nupsiErrorPayload.Kartennummer[0] : nupsiErrorPayload.Kartennummer) : (nupsiErrorPayload.errors && (nupsiErrorPayload.errors.Kartennummer ? nupsiErrorPayload.errors.Kartennummer[0] : nupsiErrorPayload.errors.kartennummer[0])) }}
                </div>
              </div>
            </div>

            <div v-if="selectedMediumType === 'rfid'">
              <p>Die alte RFID wird ungültig gesetzt. Es wird eine E-Mail an den Kunden versendet, dass das neue Medium in Kürze verschickt wird.
                 Bitte die RFID bei Lisy anlegen und alte RFID sperren!
              </p>
              <div class="mb-3">
                <label class="form-label">Neue RFID</label>
                <input type="text" :class="['form-control', (nupsiErrorPayload && (nupsiErrorPayload.rfid || nupsiErrorPayload.errors && nupsiErrorPayload.errors.rfid)) ? 'is-invalid' : '']" v-model="newRfid" placeholder="Neue RFID eingeben">
                <div style="margin-top: 2em;display: flex;align-items: center;">
                    <div class="checkbox-container">
                        <input type="checkbox" v-model="sendEmail">
                        <div class="check"></div>
                    </div>
                    <div style="margin-left: 5px;">Kunden benachrichtigen?</div>
                </div>
                <div class="invalid-feedback" v-if="nupsiErrorPayload && (nupsiErrorPayload.rfid || (nupsiErrorPayload.errors && nupsiErrorPayload.errors.rfid))">
                  {{ nupsiErrorPayload.rfid ? (Array.isArray(nupsiErrorPayload.rfid) ? nupsiErrorPayload.rfid[0] : nupsiErrorPayload.rfid) : (nupsiErrorPayload.errors && nupsiErrorPayload.errors.rfid ? nupsiErrorPayload.errors.rfid[0] : '') }}
                </div>
              </div>
            </div>

          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-primary" data-bs-dismiss="modal">Abbrechen</button>
            <button type="button" class="btn btn-primary" :disabled="isLoadingNupsi" @click="submitMediumLost">
                <i :class="[isLoadingNupsi ? 'fa-solid fa-spinner fa-spin' : '']" :disabled="isLoadingNupsi"></i>
                Zugangsmedium ersetzen
            </button>
          </div>
        </div>
      </div>
    </div>
</template>

<style scoped>
.shadow-warning {
  filter: drop-shadow(0 0 3px #ffc107);
}

.shadow-danger {
  filter: drop-shadow(0 0 3px #dc3545);
}

.table-warning {
  background-color: #fff3cd !important;
}

.table-danger {
  background-color: #f8d7da !important;
}

.status-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  display: inline-block;
  flex: 0 0 auto;
}
.status-active {
  background-color: #28a745;
  box-shadow: 0 0 6px rgba(40,167,69,0.6);
}
.status-inactive {
  background-color: #c21820;
  box-shadow: 0 0 6px rgba(87, 12, 18, 0.6);
}


.modern-radio-card {
  display: flex;
  cursor: pointer;
  width: 6em;
  user-select: none;
  margin: 0;
  justify-content: center;
}

.modern-radio-card .card-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 0.3rem 0.3rem;
  border: 2px solid #e2e8f0;
  border-radius: 0.75rem;
  transition: all 0.2s ease-in-out;
  gap: 3px;
  width: 100%;
}

.modern-radio-card .card-title {
  font-weight: 800;
  font-size: 1rem;
}

.modern-radio-card .card-content i {
  font-size: 1.5rem;
  transition: transform 0.2s ease-in-out;
}


.modern-radio-card:not(.disabled):hover .card-content {
  border-color: #0d6efd;
  transform: translateY(-2px);
}

.modern-radio-card:not(.disabled):hover input:not(:checked) + .card-content {
  background-color: #f8faff;
  color: #4a5568;
}

.modern-radio-card input:checked + .card-content {
  border-color: #0d6efd;
  background-color: #0d6efd;
  color: #ffffff;
  box-shadow: 0 6px 16px rgba(13, 110, 253, 0.25);
}


.modern-radio-card:not(.disabled):hover input:checked + .card-content {
  background-color: #0b5ed7;
  border-color: #0b5ed7;
  color: #ffffff;
}




.modern-radio-card.disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.modern-radio-card.disabled .card-content {
  background-color: #f8f9fa;
  border-color: #e9ecef;
  color: #adb5bd;
}

.div-button-modal-contract{
    display: flex;
    flex-direction: column;
    gap: 10px;
}
</style>
